import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import SiteProofLogo from '../../components/SiteProofLogo';
import LoginForm from './components/LoginForm';
import { useAuth } from '../../contexts/AuthContext';
import { companyService } from '../../services/companyService';
import { getDefaultDashboard } from '../../utils/roleAccess';
import { useLanguage } from '../../contexts/LanguageContext';
import { supabase, clearSupabaseStorage } from '../../lib/supabase';

// ── Demo email addresses (from env vars) ────────────────────────────────────
// /login must NEVER auto-authenticate any of these as a real session.
// If one of these is the current Supabase session, we sign it out immediately.
const DEMO_EMAILS = [
  import.meta.env?.VITE_DEMO_ADMIN_EMAIL,
  import.meta.env?.VITE_DEMO_MANAGER_EMAIL,
  import.meta.env?.VITE_DEMO_WORKER_EMAIL,
  import.meta.env?.VITE_DEMO_CLIENT_EMAIL,
].filter(Boolean);

const isDemoEmail = (email) =>
  !!email && DEMO_EMAILS?.some((d) => d?.toLowerCase() === email?.toLowerCase());

const ROLE_LABELS = {
  admin: 'Admin',
  site_manager: 'Site Manager',
  worker: 'Worker',
  client: 'Client',
};

const Login = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading, roleLoading, signOut } = useAuth();
  const { t } = useLanguage();
  const [signingOut, setSigningOut] = useState(false);
  // Track whether we are in the process of clearing a stale demo session
  const [clearingDemo, setClearingDemo] = useState(false);

  // stale_session=1 flag set by /invite-setup when verifyOtp failed but a
  // partial session was minted into localStorage. The user is signed in
  // through the lucky-session path with no password set; we surface the
  // two recovery options below (set password now / sign out and restart)
  // instead of the normal "Go to dashboard" panel.
  const isStaleSession = new URLSearchParams(window.location.search)?.get('stale_session') === '1';

  // Inline set-password form state, shown when isStaleSession + user signed in
  const [stalePassword, setStalePassword] = useState('');
  const [staleConfirm, setStaleConfirm] = useState('');
  const [staleState, setStaleState] = useState('idle'); // idle | saving | saved | error
  const [staleError, setStaleError] = useState('');

  // ── HARD GUARD: if the restored session belongs to a demo account, kill it ──
  useEffect(() => {
    if (loading || roleLoading) return; // wait until auth is resolved
    if (!user) return; // no session — nothing to clear

    if (isDemoEmail(user?.email)) {
      setClearingDemo(true);
      (async () => {
        try {
          // Sign out the demo session silently
          clearSupabaseStorage();
          await supabase?.auth?.signOut();
        } catch (_) {
          // Even if signOut fails, storage is already cleared
        } finally {
          setClearingDemo(false);
        }
      })();
    }
  }, [user, loading, roleLoading]);

  // While auth is loading or we are clearing a demo session, show a spinner
  if (loading || roleLoading || clearingDemo) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent"></div>
      </div>
    );
  }

  // ── Stale-session recovery panel ────────────────────────────────────────
  // Reached when /invite-setup detected a dangling Supabase session and
  // redirected here with ?stale_session=1. The user is technically signed
  // in but has no password set, so the standard "Go to dashboard" panel is
  // a trap — signing out would lock them out. Offer two explicit choices.
  if (isStaleSession && user && !isDemoEmail(user?.email)) {
    const handleSetPassword = async (e) => {
      e?.preventDefault();
      setStaleError('');
      if (!stalePassword || stalePassword?.length < 8) {
        setStaleError('Password must be at least 8 characters.');
        setStaleState('error');
        return;
      }
      if (stalePassword !== staleConfirm) {
        setStaleError('Passwords do not match.');
        setStaleState('error');
        return;
      }
      setStaleState('saving');
      try {
        const { error } = await supabase?.auth?.updateUser({ password: stalePassword });
        if (error) {
          setStaleError(error?.message || 'Failed to set password. Please try again.');
          setStaleState('error');
          return;
        }
        setStaleState('saved');
        // Strip the stale_session flag so subsequent visits to /login don't
        // re-fire this panel for an already-recovered account.
        window.history?.replaceState({}, document.title, '/login');
      } catch (err) {
        setStaleError(err?.message || 'Something went wrong.');
        setStaleState('error');
      }
    };

    const handleStaleSignOut = async () => {
      setSigningOut(true);
      try {
        clearSupabaseStorage();
        await signOut();
      } catch (_) {
        // signOut may fail if the dangling session is malformed — storage
        // is cleared either way, which is the important bit.
      }
      // Hard reload to /login (no params) so the form mounts fresh with
      // no residual session in the AuthContext.
      window.location.href = '/login';
    };

    const handleGoToDashboardAfterSave = () => {
      const role = userProfile?.role;
      navigate(role ? getDefaultDashboard(role) : '/admin-dashboard', { replace: true });
    };

    return (
      <div className="min-h-screen bg-background flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4 py-8 md:py-12">
          <div className="w-full max-w-md">
            <div className="bg-card rounded-lg shadow-elevation-3 p-6 md:p-8 border border-border">
              <div className="flex flex-col items-center mb-6">
                <div className="mb-4 w-full flex justify-center">
                  <SiteProofLogo size="lg" iconOnly />
                </div>
              </div>

              {staleState === 'saved' ? (
                <>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-5">
                    <div className="flex items-start gap-3">
                      <Icon name="CheckCircle2" size={20} color="#16a34a" className="flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-emerald-900 mb-1">Password set</p>
                        <p className="text-sm text-emerald-800">You can now sign in with your email and the password you just chose.</p>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleGoToDashboardAfterSave}
                    className="w-full flex items-center justify-center gap-2 bg-accent hover:bg-accent/90 text-white font-semibold py-3 px-4 rounded-lg transition-smooth"
                  >
                    <Icon name="LayoutDashboard" size={16} />
                    Go to Dashboard
                  </button>
                </>
              ) : (
                <>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-5">
                    <div className="flex items-start gap-3">
                      <Icon name="AlertTriangle" size={20} color="#d97706" className="flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-amber-900 mb-1">You're signed in, but your password isn't set</p>
                        <p className="text-sm text-amber-800 leading-relaxed">
                          We signed you in using your invite link, but you haven't created a password yet. Pick one of the options below to keep your access secure.
                        </p>
                      </div>
                    </div>
                  </div>

                  <form onSubmit={handleSetPassword} className="space-y-4 mb-4">
                    <h3 className="text-sm font-semibold text-foreground">Option 1 — Set your password now</h3>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">New password</label>
                      <input
                        type="password"
                        value={stalePassword}
                        onChange={(e) => {
                          setStalePassword(e?.target?.value);
                          if (staleState === 'error') {
                            setStaleState('idle');
                            setStaleError('');
                          }
                        }}
                        placeholder="At least 8 characters"
                        autoComplete="new-password"
                        disabled={staleState === 'saving'}
                        className="w-full px-4 py-2.5 rounded-lg bg-background border border-border text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent text-sm transition disabled:opacity-60 disabled:cursor-not-allowed"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">Confirm password</label>
                      <input
                        type="password"
                        value={staleConfirm}
                        onChange={(e) => setStaleConfirm(e?.target?.value)}
                        placeholder="Re-enter your password"
                        autoComplete="new-password"
                        disabled={staleState === 'saving'}
                        className="w-full px-4 py-2.5 rounded-lg bg-background border border-border text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent text-sm transition disabled:opacity-60 disabled:cursor-not-allowed"
                      />
                    </div>

                    {staleState === 'error' && staleError && (
                      <div className="bg-destructive/10 border border-destructive/20 rounded-lg px-4 py-3">
                        <p className="text-sm text-destructive">{staleError}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={staleState === 'saving'}
                      className="w-full flex items-center justify-center gap-2 bg-accent hover:bg-accent/90 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg transition-smooth"
                    >
                      {staleState === 'saving' ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                          Setting password…
                        </>
                      ) : (
                        'Set my password now'
                      )}
                    </button>
                  </form>

                  <div className="pt-4 border-t border-border">
                    <h3 className="text-sm font-semibold text-foreground mb-2">Option 2 — Sign out and start fresh</h3>
                    <p className="text-xs text-muted-foreground mb-3">
                      Sign out of this half-finished session. You can use the fresh invite link from your inbox to restart setup.
                    </p>
                    <button
                      onClick={handleStaleSignOut}
                      disabled={signingOut}
                      className="w-full flex items-center justify-center gap-2 bg-transparent hover:bg-muted border border-border text-foreground font-medium py-3 px-4 rounded-lg transition-smooth"
                    >
                      {signingOut ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-foreground" />
                      ) : (
                        <Icon name="LogOut" size={16} />
                      )}
                      {signingOut ? 'Signing out…' : 'Sign out and start fresh'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        <footer className="py-6 px-4 border-t border-border bg-card">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Icon name="MapPin" size={16} className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {t('login.serving_uk')}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {t('login.copyright')?.replace('{year}', new Date()?.getFullYear())}
              </p>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  // ── Authenticated user: show explicit "already signed in" panel ──
  // GUARD: never show this panel for demo accounts (cleared above, but double-check)
  if (user && !isDemoEmail(user?.email)) {
    const role = userProfile?.role;
    const roleLabel = ROLE_LABELS?.[role] || 'User';

    const handleGoToDashboard = async () => {
      if (role === 'admin') {
        try {
          const { data, error: companyError } = await companyService?.getUserCompany();

          if (companyError || !data) {
            navigate('/admin-dashboard', { replace: true });
            return;
          }

          if (data?.id) {
            navigate('/admin-dashboard', { replace: true });
          } else {
            navigate('/setup-flow-plan-confirmation', { replace: true });
          }
        } catch {
          navigate('/admin-dashboard', { replace: true });
        }
      } else if (role) {
        navigate(getDefaultDashboard(role), { replace: true });
      } else {
        navigate('/admin-dashboard', { replace: true });
      }
    };

    const handleSignOut = async () => {
      setSigningOut(true);
      await signOut();
      setSigningOut(false);
    };

    return (
      <div className="min-h-screen bg-background flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4 py-8 md:py-12">
          <div className="w-full max-w-md">
            <div className="bg-card rounded-lg shadow-elevation-3 p-6 md:p-8 border border-border">
              {/* Logo */}
              <div className="flex flex-col items-center mb-8">
                <div className="mb-4 w-full flex justify-center">
                  <SiteProofLogo size="lg" iconOnly />
                </div>
              </div>

              {/* Already signed in banner */}
              <div className="bg-accent/10 border border-accent/20 rounded-lg p-5 mb-6 text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Icon name="CheckCircle" size={18} color="var(--color-accent)" />
                  <span className="text-sm font-semibold text-accent">You're already signed in</span>
                </div>
                <p className="text-sm text-muted-foreground mb-1">
                  Signed in as <span className="font-medium text-foreground">{user?.email}</span>
                </p>
                <p className="text-xs text-muted-foreground">
                  Role: <span className="font-medium text-foreground">{roleLabel}</span>
                </p>
              </div>

              {/* Go to dashboard */}
              <button
                onClick={handleGoToDashboard}
                className="w-full flex items-center justify-center gap-2 bg-accent hover:bg-accent/90 text-white font-semibold py-3 px-4 rounded-lg transition-smooth mb-3"
              >
                <Icon name="LayoutDashboard" size={16} />
                Go to Dashboard
              </button>

              {/* Sign out */}
              <button
                onClick={handleSignOut}
                disabled={signingOut}
                className="w-full flex items-center justify-center gap-2 bg-transparent hover:bg-muted border border-border text-foreground font-medium py-3 px-4 rounded-lg transition-smooth"
              >
                {signingOut ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-foreground" />
                ) : (
                  <Icon name="LogOut" size={16} />
                )}
                {signingOut ? 'Signing out…' : 'Sign out and switch account'}
              </button>
            </div>
          </div>
        </div>
        <footer className="py-6 px-4 border-t border-border bg-card">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Icon name="MapPin" size={16} className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {t('login.serving_uk')}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {t('login.copyright')?.replace('{year}', new Date()?.getFullYear())}
              </p>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  // ── Unauthenticated user (or demo session just cleared): show the normal login form ──
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-8 md:py-12">
        <div className="w-full max-w-md">
          <div className="bg-card rounded-lg shadow-elevation-3 p-6 md:p-8 border border-border">
            {/* Logo + branding */}
            <div className="flex flex-col items-center mb-8">
              <div className="mb-4 flex justify-center w-full">
                <SiteProofLogo size="lg" iconOnly />
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground">
                  {t('login.tagline')}
                </p>
              </div>
            </div>

            {/* ── Login form heading ── */}
            <div className="mb-2">
              <h2 className="text-xl font-semibold text-foreground mb-1 font-heading">
                {t('login.sign_in_heading')}
              </h2>
              <p className="text-sm text-muted-foreground">
                {t('login.sign_in_subheading')}
              </p>
            </div>

            <LoginForm />

            <div className="mt-6 pt-6 border-t border-border text-center">
              <p className="text-sm text-muted-foreground">
                {t('login.no_account')}{' '}
                <Link
                  to="/company-registration"
                  className="font-medium text-accent hover:text-accent/80 transition-smooth"
                >
                  {t('login.register_company')}
                </Link>
              </p>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-muted-foreground leading-relaxed">
              {t('login.terms')}
              <br />
              {t('login.disclaimer')}
            </p>
          </div>
        </div>
      </div>
      <footer className="py-6 px-4 border-t border-border bg-card">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Icon name="MapPin" size={16} className="text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {t('login.serving_uk')}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              {t('login.copyright')?.replace('{year}', new Date()?.getFullYear())}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Login;